// Google Calendar persistence utility

// localStorage keys for Google Calendar data
export const GOOGLE_CALENDAR_STORAGE_KEYS = {
    CALENDAR_ID: 'googleCalendar_calendarId',
    ACCESS_TOKEN: 'googleCalendar_accessToken',
    REFRESH_TOKEN: 'googleCalendar_refreshToken',
    EXPIRY_DATE: 'googleCalendar_expiryDate',
    TOKEN_TYPE: 'googleCalendar_tokenType',
    SCOPE: 'googleCalendar_scope',
    CONNECTION_STATUS: 'googleCalendar_connectionStatus',
    LAST_SYNC_TIME: 'googleCalendar_lastSyncTime'
};

// Get Google Calendar data from localStorage
export const getGoogleCalendarData = (key, defaultValue = null) => {
    try {
        const saved = localStorage.getItem(key);
        if (saved === null) return defaultValue;

        // Try to parse as JSON first
        try {
            return JSON.parse(saved);
        } catch {
            // If parsing fails, return as string
            return saved;
        }
    } catch (error) {
        console.warn(`Failed to get Google Calendar data for key: ${key}`, error);
        return defaultValue;
    }
};

// Set Google Calendar data in localStorage
export const setGoogleCalendarData = (key, value) => {
    try {
        const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
        localStorage.setItem(key, serializedValue);
    } catch (error) {
        console.warn(`Failed to persist Google Calendar data for key: ${key}`, error);
    }
};

// Save all Google Calendar connection data
export const saveGoogleCalendarConnectionData = (data) => {
    try {
        const {
            calendarId,
            accessToken,
            refreshToken,
            expiryDate,
            tokenType,
            scope
        } = data;

        // Save individual pieces of data
        if (calendarId) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CALENDAR_ID, calendarId);
        if (accessToken) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        if (refreshToken) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        if (expiryDate) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.EXPIRY_DATE, expiryDate);
        if (tokenType) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.TOKEN_TYPE, tokenType);
        if (scope) setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.SCOPE, scope);
        
        // Set connection status and last sync time
        setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CONNECTION_STATUS, 'connected');
        setGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.LAST_SYNC_TIME, new Date().toISOString());

    } catch (error) {
        console.warn('Failed to save Google Calendar connection data:', error);
    }
};

// Get all Google Calendar connection data
export const getGoogleCalendarConnectionData = () => {
    try {
        return {
            calendarId: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CALENDAR_ID),
            accessToken: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.ACCESS_TOKEN),
            refreshToken: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.REFRESH_TOKEN),
            expiryDate: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.EXPIRY_DATE),
            tokenType: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.TOKEN_TYPE),
            scope: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.SCOPE),
            connectionStatus: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CONNECTION_STATUS),
            lastSyncTime: getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.LAST_SYNC_TIME)
        };
    } catch (error) {
        console.warn('Failed to get Google Calendar connection data:', error);
        return {};
    }
};

// Clear all Google Calendar data from localStorage
export const clearGoogleCalendarData = () => {
    Object.values(GOOGLE_CALENDAR_STORAGE_KEYS).forEach(key => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove Google Calendar data for key: ${key}`, error);
        }
    });
};

// Check if Google Calendar tokens are expired
export const isGoogleCalendarTokenExpired = () => {
    try {
        const expiryDate = getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.EXPIRY_DATE);
        if (!expiryDate) return true;
        
        const now = new Date();
        const expiry = new Date(expiryDate);
        
        // Consider token expired if it expires within the next 5 minutes
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
        return (expiry.getTime() - now.getTime()) <= bufferTime;
    } catch (error) {
        console.warn('Failed to check Google Calendar token expiry:', error);
        return true;
    }
};

// Check if Google Calendar is connected
export const isGoogleCalendarConnected = () => {
    try {
        const connectionStatus = getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CONNECTION_STATUS);
        const calendarId = getGoogleCalendarData(GOOGLE_CALENDAR_STORAGE_KEYS.CALENDAR_ID);
        return connectionStatus === 'connected' && !!calendarId;
    } catch (error) {
        console.warn('Failed to check Google Calendar connection status:', error);
        return false;
    }
};
